import React from "react";
import { Link } from "react-router-dom";
import { PawPrint, Heart, Leaf, Building2, Recycle, ArrowRight } from "lucide-react";
import Reveal from "../Reveal";

const topFeatures = [
  { icon: Heart, title: "Compassionate Care", text: "Our friendly team is committed to giving the best customer service to you and your pets." },
  { icon: Leaf, title: "Holistic Wellness", text: "We use the word holistic with confidence — and a clear definition: we treat the whole animal, using every tool available." },
  { icon: Building2, title: "Modern Facilities", text: "From routine check-ups to complex treatment — including consultations, preventative care and out-of-hours emergency support." },
  { icon: Recycle, title: "Sustainable Practice", text: "We equip members of the veterinary profession with the tools they need to drive sustainability in their work." },
];

const linkFeatures = [
  { title: "Compassionate Care", text: "Friendly and caring service, with no-rush 15-minute consultation appointments." },
  { title: "Personalised Attention", text: "As a family-run practice, we take time to get to know each pet and owner — providing tailored care, whether advanced diagnostics, ongoing management of a chronic condition, or a routine check-up." },
  { title: "Trusted Expertise", text: "Most of our full-time vets hold or are undertaking certificates, forming our network of excellence — from advanced surgery to imaging, dentistry and dermatology. Skilled, friendly and approachable, with extensive customer-service training to ease the worry." },
  { title: "Effortless Experience", text: "We make your visit easy and convenient — open 6 days a week with free parking outside. We accommodate most treatments in-house, or nearby if needed, avoiding lengthy travel to expensive referral centres." },
];

const WhyChoose = () => {
  return (
    <section className="bg-pawelle-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1290px] px-5">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left copy + top features */}
          <div>
            <Reveal animation="rubberBand">
              <span className="inline-flex items-center gap-2 font-display text-[15px] font-semibold text-pawelle-primary">
                <PawPrint className="h-5 w-5" />
                Why Choose Us
              </span>
            </Reveal>
            <Reveal animation="fadeInUp" delay={100}>
              <h2 className="mt-4 max-w-lg font-display text-[30px] font-bold leading-tight text-pawelle-ink md:text-[38px]">
                Uniting Innovation and Empathy in Every Paw
              </h2>
            </Reveal>
            <Reveal animation="fadeInUp" delay={150}>
              <p className="mt-4 max-w-lg font-body text-[16px] leading-relaxed text-pawelle-body">
                From routine check-ups to more complex treatment, our team is here to provide inspiring vet care to
                pet owners across the region.
              </p>
            </Reveal>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {topFeatures.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.title} animation="fadeInUp" delay={i * 80} className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-[0_10px_30px_rgba(67,48,109,0.05)]">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-pawelle-bg text-pawelle-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h4 className="font-display text-[16px] font-semibold text-pawelle-ink">{f.title}</h4>
                      <p className="mt-1 font-body text-[13px] leading-relaxed text-pawelle-body">{f.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          {/* Right list */}
          <div className="grid gap-5">
            {linkFeatures.map((f, i) => (
              <Reveal
                key={f.title}
                animation="fadeInRight"
                delay={i * 80}
                className="group flex items-start justify-between rounded-2xl bg-white p-6 shadow-[0_10px_30px_rgba(67,48,109,0.05)] transition-all duration-300 hover:bg-pawelle-primary"
              >
                <div>
                  <h4 className="font-display text-[19px] font-semibold text-pawelle-ink transition-colors group-hover:text-white">{f.title}</h4>
                  <p className="mt-1.5 font-body text-[14px] leading-relaxed text-pawelle-body transition-colors group-hover:text-white/85">
                    {f.text}
                  </p>
                </div>
                <Link
                  to="/gb/en/services.html"
                  className="ml-4 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-pawelle-bg text-pawelle-primary transition-colors group-hover:bg-white"
                  aria-label="Learn more"
                >
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
